import { trim } from 'lodash';
import { FormProvider, useForm } from 'react-hook-form';
import { Masks } from 'react-native-mask-input';
import { Button, Layout } from '../../../components/core';
import { FormErrorSubmit, FormInput, FormInputMasked, FormSelect } from '../../../components/form';
import { convertDateForDisplay, DATE_REGEX } from '../../../utils/date';
import { useOnboardUser } from '../hooks/useOnboardUser';

type OnboardingFormProps = {
  defaultUserName: string;
  currentBirthDate?: string;
  onConsentBlocked: () => void;
  onSaved: (options: { skipConsents: boolean }) => void;
  orgSelectOptions: Array<{ label: string; value: number }>;
};

export const OnboardingForm = ({
  currentBirthDate,
  defaultUserName,
  onConsentBlocked,
  onSaved,
  orgSelectOptions,
}: OnboardingFormProps): JSX.Element => {
  const orgSelectIsActive = orgSelectOptions.length > 0;

  const formContext = useForm({
    defaultValues: {
      name: defaultUserName,
      birthDate: convertDateForDisplay(currentBirthDate) ?? '',
      organizationIdSelection: undefined,
      submit: undefined,
    },
  });

  const accumulator: Record<string, string> = {};
  const orgOptions = orgSelectOptions.reduce(
    (acc, { label, value }) => ({
      ...acc,
      [String(value)]: label,
    }),
    accumulator,
  );

  const hasPrefilledBirthDate = Boolean(currentBirthDate);

  const [onboardUser, { loading }] = useOnboardUser({
    onConsentBlocked: (): void => {
      onConsentBlocked();
    },
    onCompleted: (options): void => {
      onSaved(options);
    },
    onError: saveNameError => {
      formContext.setError('submit', saveNameError);
    },
  });

  const {
    clearErrors,
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = formContext;

  const organizationIdSelectionField = watch('organizationIdSelection');
  const submitDisabled =
    orgSelectIsActive &&
    (organizationIdSelectionField === undefined || organizationIdSelectionField === '');

  const submitForm = (): void => {
    // Make sure to clear the errors first, or any submit errors will prevent form submission.
    clearErrors();

    void handleSubmit(({ name, birthDate, organizationIdSelection }): void => {
      const organizationId =
        organizationIdSelection !== undefined ? parseInt(organizationIdSelection, 10) : undefined;

      void onboardUser(trim(name), birthDate, organizationId);
    })();
  };

  return (
    <FormProvider {...formContext}>
      <Layout.VStack space={8} width="100%">
        <Layout.VStack space={4}>
          <FormInput
            label="What should we call you?"
            helperText="Not required to be your legal name"
            name="name"
            placeholder="Name"
            autoComplete="name"
            autoCapitalize="none"
            control={control}
            error={errors.name}
            maxLength={100}
            necessityIndicator
          />

          <FormInputMasked
            label="Date of Birth"
            mask={Masks.DATE_MMDDYYYY}
            name="birthDate"
            placeholder="MM/DD/YYYY"
            autoComplete="birthdate-full"
            autoCapitalize="none"
            control={control}
            error={errors.birthDate}
            isDisabled={hasPrefilledBirthDate}
            isRequired
            necessityIndicator
            rules={{
              pattern: {
                value: DATE_REGEX,
                message: 'You must enter a valid date MM/DD/YYYY.',
              },
            }}
          />

          {orgSelectIsActive && (
            <FormSelect
              control={control}
              error={errors.organizationIdSelection}
              label="Select your Campus"
              placeholder="Please select your campus"
              isRequired
              name="organizationIdSelection"
              necessityIndicator
              options={orgOptions}
            />
          )}
        </Layout.VStack>

        <Layout.VStack space={4}>
          {errors.submit && <FormErrorSubmit>{errors.submit.message}</FormErrorSubmit>}

          <Button.primaryLarge
            isLoading={loading}
            testID="button-onboarding-submit"
            onPress={submitForm}
            isDisabled={submitDisabled}
          >
            Get Started
          </Button.primaryLarge>
        </Layout.VStack>
      </Layout.VStack>
    </FormProvider>
  );
};
